/**
 * Quick access to FlahaSOIL's most frequent route-based workflows.
 *
 * The dock is navigation, not window state: every destination is a real
 * React Router link and the mobile "More" action opens the complete drawer.
 */
import {
	Box,
	ButtonBase,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import AssessmentIcon from "@mui/icons-material/Assessment";
import DashboardIcon from "@mui/icons-material/Dashboard";
import FolderIcon from "@mui/icons-material/Folder";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ScienceIcon from "@mui/icons-material/Science";
import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";

import { reducedMotionMediaQuery } from "../../theme/motionTokens";
import { GlassSurface } from "./GlassSurface";

interface DockItem {
	label: string;
	shortLabel: string;
	to: string;
	icon: ReactNode;
	exact?: boolean;
	hideOnPhone?: boolean;
}

const DOCK_ITEMS: DockItem[] = [
	{
		label: "Dashboard",
		shortLabel: "Home",
		to: "/dashboard",
		icon: <DashboardIcon fontSize="small" />,
		exact: true,
	},
	{
		label: "Projects",
		shortLabel: "Projects",
		to: "/projects",
		icon: <FolderIcon fontSize="small" />,
	},
	{
		label: "New Soil Analysis",
		shortLabel: "New",
		to: "/soil-tests/new",
		icon: <ScienceIcon fontSize="small" />,
		exact: true,
	},
	{
		label: "Reports",
		shortLabel: "Reports",
		to: "/reports",
		icon: <AssessmentIcon fontSize="small" />,
	},
	{
		label: "Scientific Standards",
		shortLabel: "Standards",
		to: "/standards",
		icon: <LibraryBooksIcon fontSize="small" />,
		exact: true,
		hideOnPhone: true,
	},
];

export interface WorkspaceDockProps {
	desktopOffset?: number;
	navigationOpen?: boolean;
	onOpenNavigation: () => void;
}

export function WorkspaceDock({
	desktopOffset = 0,
	navigationOpen = false,
	onOpenNavigation,
}: WorkspaceDockProps) {
	const theme = useTheme();
	const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
	const prefersReducedMotion = useMediaQuery(
		"(prefers-reduced-motion: reduce)",
		{ noSsr: true }
	);

	return (
		<GlassSurface
			component="nav"
			aria-label="Quick workflow navigation"
			data-motion={prefersReducedMotion ? "reduced" : "full"}
			intensity="strong"
			depth="floating"
			sx={{
				position: "fixed",
				zIndex: theme.zIndex.appBar - 1,
				bottom: { xs: 0, sm: 14 },
				left: {
					xs: 0,
					sm: "50%",
					md: `calc(50% + ${desktopOffset}px)`,
				},
				right: { xs: 0, sm: "auto" },
				transform: { xs: "none", sm: "translateX(-50%)" },
				width: { xs: "100%", sm: "auto" },
				maxWidth: { xs: "none", sm: "calc(100vw - 32px)" },
				borderRadius: { xs: "16px 16px 0 0", sm: 2.25 },
				px: { xs: 0.5, sm: 1 },
				py: { xs: 0.5, sm: 0.75 },
				paddingBottom: { xs: "max(4px, env(safe-area-inset-bottom))", sm: 0.75 },
				overflow: "visible",
				[reducedMotionMediaQuery]: {
					transition: "none",
				},
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "stretch",
					justifyContent: "center",
					gap: { xs: 0, sm: 0.5, md: 0.75 },
				}}
			>
				{DOCK_ITEMS.filter((item) => !(isPhone && item.hideOnPhone)).map((item) => (
					<Tooltip key={item.to} title={item.label} enterDelay={500}>
						<ButtonBase
							component={NavLink}
							to={item.to}
							end={item.exact ?? false}
							aria-label={item.label}
							sx={{
								display: "flex",
								flex: { xs: "1 1 0", sm: "0 0 auto" },
								minWidth: { xs: 0, sm: 74, md: 88 },
								minHeight: { xs: 52, sm: 62 },
								px: { xs: 0.25, sm: 1 },
								py: 0.5,
								borderRadius: 1.5,
								color: "workspace.textSecondary",
								flexDirection: "column",
								gap: 0.25,
								touchAction: "manipulation",
								WebkitTapHighlightColor: "transparent",
								transition: theme.motion.transition.surface,
								"& .dock-icon": {
									display: "grid",
									placeItems: "center",
									width: { xs: 30, sm: 34 },
									height: { xs: 30, sm: 34 },
									borderRadius: 1.25,
									transition: theme.motion.transition.surface,
								},
								"&:hover, &:focus-visible": {
									color: "workspace.textPrimary",
									backgroundColor: "rgba(246,240,227,0.07)",
									"& .dock-icon": {
										transform: { xs: "none", sm: "translateY(-2px) scale(1.06)" },
									},
								},
								"&.active": {
									color: "workspace.textPrimary",
									backgroundColor: "rgba(119,169,87,0.16)",
									boxShadow: `inset 0 0 0 1px ${theme.palette.workspace.green}`,
									"& .dock-icon": { color: "workspace.green" },
								},
								[reducedMotionMediaQuery]: {
									transition: "none",
									"& .dock-icon": { transition: "none", transform: "none" },
								},
							}}
						>
							<Box className="dock-icon" aria-hidden="true">
								{item.icon}
							</Box>
							<Typography
								component="span"
								sx={{
									fontSize: { xs: 10, sm: 11 },
									fontWeight: 650,
									lineHeight: 1.1,
									whiteSpace: "nowrap",
								}}
							>
								{isPhone ? item.shortLabel : item.label}
							</Typography>
						</ButtonBase>
					</Tooltip>
				))}

				{isPhone && (
					<Tooltip title="Open complete navigation">
						<ButtonBase
							onClick={onOpenNavigation}
							aria-label="Open complete navigation"
							aria-controls="mobile-navigation"
							aria-expanded={navigationOpen}
							sx={{
								flex: "1 1 0",
								minWidth: 0,
								minHeight: 52,
								borderRadius: 1.5,
								color: "workspace.textSecondary",
								flexDirection: "column",
								gap: 0.25,
								touchAction: "manipulation",
								WebkitTapHighlightColor: "transparent",
								"&:hover, &:focus-visible": {
									color: "workspace.textPrimary",
									backgroundColor: "rgba(246,240,227,0.07)",
								},
								"&:focus-visible": {
									outline: `2px solid ${theme.palette.workspace.blue}`,
									outlineOffset: 2,
								},
							}}
						>
							<MoreHorizIcon aria-hidden="true" />
							<Typography component="span" sx={{ fontSize: 10, fontWeight: 650 }}>
								More
							</Typography>
						</ButtonBase>
					</Tooltip>
				)}
			</Box>
		</GlassSurface>
	);
}
